import React, { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { Input, Button } from '@iq/react-components';
import Heading from '../../../../Heading/Heading';
import NotificationFilter from '../NotificationFilter';
import UserList from './UserList';
import AddCustomizationModal from './AddCustomizationModal';
import { getUserNotificationConfigs } from '../../../../../bundles/notification-config';
import { getMembers } from '../../../../../bundles/auth';
import { getUsers } from '../../../../../bundles/ad';
import { getActiveSite } from '../../../../../bundles/sites';
import { DELIVERY_FILTER_OPTIONS, isConfigCustom } from '../../constants';

const UsersTab = () => {
  const [searchText, setSearchText] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [activeFilters, setActiveFilters] = useState({ user: [], role: [], delivery: [] });
  const userConfigs = useSelector(getUserNotificationConfigs);
  const site = useSelector(getActiveSite);
  const siteMembers = useSelector((state) => getMembers(state, `site/${site?.id}`));
  const users = useSelector(getUsers);

  // All users on this site — used by AddCustomizationModal to show who can be added.
  const siteUsers = useMemo(() => {
    if (!site?.id || siteMembers.length === 0) return [];
    const customUserOids = new Set(userConfigs.map((c) => c.user));
    const siteUserMap = new Map();
    for (const member of siteMembers) {
      for (const conn of member.connected || []) {
        if (conn.startsWith('users/')) {
          const userId = conn.split('/')[1];
          if (!siteUserMap.has(userId)) {
            siteUserMap.set(userId, member.type);
          }
        }
      }
    }
    return [...siteUserMap.entries()]
      .map(([userId, roleName]) => {
        const user = users.find((u) => u.id === userId);
        if (!user?.enabled || !user?.email) return null;
        return {
          id: userId,
          user: user.email,
          name: user.name,
          roleName,
          hasCustomConfig: customUserOids.has(userId),
        };
      })
      .filter(Boolean);
  }, [siteMembers, users, site?.id, userConfigs]);

  // Only users with a custom user-level config — shown in UserList.
  const usersWithConfigs = useMemo(() => {
    return userConfigs.map((config) => {
      const user = users.find((u) => u.id === config.user);
      const member = siteMembers.find((m) => (m.connected || []).includes(`users/${config.user}`));
      const roleName = member?.type || null;
      const roleId = member?.id || config.role;
      const hasCustomConfig = isConfigCustom(roleName, config);

      return {
        id: user?.id || config.user,
        user: user?.email,
        name: user?.name || config.user,
        roleName,
        roleId,
        configId: config.id,
        hasCustomConfig,
        stateChangeSettings: config.stateChangeSettings,
      };
    });
  }, [userConfigs, users, siteMembers]);

  const userOptions = useMemo(() => {
    return usersWithConfigs.map((u) => ({ value: u.user, label: u.user }));
  }, [usersWithConfigs]);

  const roleOptions = useMemo(() => {
    return siteMembers.map((m) => ({ value: m.type, label: m.type }));
  }, [siteMembers]);

  const filteredUsers = useMemo(() => {
    let result = usersWithConfigs;
    if (searchText) {
      const search = searchText.toLowerCase();
      result = result.filter(
        (u) => u.user?.toLowerCase().includes(search) || u.name?.toLowerCase().includes(search)
      );
    }
    if (activeFilters.user.length > 0) {
      const emails = new Set(activeFilters.user.map((u) => u.value));
      result = result.filter((u) => emails.has(u.user));
    }
    if (activeFilters.role.length > 0) {
      const roleNames = new Set(activeFilters.role.map((r) => r.value));
      result = result.filter((u) => roleNames.has(u.roleName));
    }
    if (activeFilters.delivery.length > 0) {
      const deliveryKeys = new Set(activeFilters.delivery.map((d) => d.value));
      result = result.filter((u) => {
        const settings = u.stateChangeSettings || {};
        return Object.values(settings).some((s) =>
          [...deliveryKeys].some((key) => s[key] === true)
        );
      });
    }
    return result;
  }, [usersWithConfigs, searchText, activeFilters]);

  const filters = useMemo(
    () => [
      { key: 'user', label: 'User', options: userOptions },
      { key: 'role', label: 'Role', options: roleOptions },
      { key: 'delivery', label: 'Delivery Options', options: DELIVERY_FILTER_OPTIONS },
    ],
    [userOptions, roleOptions]
  );

  return (
    <div className="users-tab">
      <Heading
        contentLeft={
          <>
            <Input
              placeholder="Search..."
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
            />
            <NotificationFilter
              filters={filters}
              onApply={setActiveFilters}
              onReset={() => setActiveFilters({ user: [], role: [], delivery: [] })}
            />
          </>
        }
        contentRight={
          <Button
            activity="primary"
            onClick={() => setShowAddModal(true)}
          >
            Add customization
          </Button>
        }
      />
      <UserList users={filteredUsers} />
      {showAddModal && (
        <AddCustomizationModal
          siteUsers={siteUsers}
          onClose={() => setShowAddModal(false)}
        />
      )}
    </div>
  );
};

export default UsersTab;
